import React, { useState, useEffect, useRef } from 'react';
import './App.css';

const QUESTIONS = [
  {
    id: 'clientName',
    question: "What's the client's name?",
    placeholder: "e.g., Club Doctor",
    type: 'text',
    required: true
  },
  {
    id: 'brandUrl',
    question: "What's the brand's website URL?",
    placeholder: "https://clubdoctorgolf.com",
    type: 'text',
    required: true,
    helpText: "We'll analyze this to understand brand positioning, voice, and customer avatars"
  },
  {
    id: 'googleDriveFolderUrl',
    question: "What's the Google Drive Folder URL?",
    placeholder: "https://drive.google.com/drive/folders/...",
    type: 'text',
    required: true,
    helpText: "Paste the full URL of the client's Drive folder where campaign files will be saved"
  },
  {
    id: 'campaignCalendarSheetUrl',
    question: "What's the Campaign Calendar Sheet URL?",
    placeholder: "https://docs.google.com/spreadsheets/d/...",
    type: 'text',
    required: true,
    helpText: "The Google Sheet with Email tab containing campaign strategies (angle, struggle, solution, etc.)"
  },
  {
    id: 'productCatalogLocation',
    question: "Product catalog location (optional)",
    placeholder: "Leave blank to use main website",
    type: 'text',
    required: false,
    helpText: "Only fill if products are on a separate URL (e.g., Shopify store, product sheet)"
  },
  {
    id: 'brandVoiceGuidelines',
    question: "Additional brand voice notes (optional)",
    placeholder: "Any specific tone requirements, words to avoid, or brand personality notes...",
    type: 'textarea',
    required: false,
    helpText: "Optional: Add any brand voice details beyond what we'll discover from the website"
  }
];

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [currentValue, setCurrentValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);

  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;

  useEffect(() => {
    // Focus input when question changes
    if (inputRef.current && currentStep < QUESTIONS.length) {
      inputRef.current.focus();
    }
    
    // Load saved value for current question
    setCurrentValue(formData[currentQuestion?.id] || '');
  }, [currentStep, currentQuestion, formData]);

  const handleNext = () => {
    if (currentQuestion.required && !currentValue.trim()) {
      return; // Don't advance if required field is empty
    }

    // Save current value
    setFormData(prev => ({
      ...prev,
      [currentQuestion.id]: currentValue
    }));

    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && currentQuestion.type !== 'textarea') {
      e.preventDefault();
      handleNext();
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    const dataToSubmit = {
      ...formData,
      [currentQuestion.id]: currentValue
    };

    try {
      const response = await fetch('/api/onboard-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSubmit),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert('Failed to submit. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Network error. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="App">
        <div className="success-screen">
          <div className="success-icon">✓</div>
          <h1>Client Onboarded Successfully!</h1>
          <p>The campaign agent is now configured for <strong>{formData.clientName}</strong></p>
          <button 
            className="start-over-btn"
            onClick={() => {
              setSubmitted(false);
              setCurrentStep(0);
              setFormData({});
              setCurrentValue('');
            }}
          >
            Onboard Another Client
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="app-header">
        <img src="/weclick-logo.png" alt="WeClick Group" className="logo" />
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <div className="question-container">
        <div className="question-number">
          {currentStep + 1} / {QUESTIONS.length}
        </div>

        <h1 className="question-text">
          {currentQuestion.question}
          {!currentQuestion.required && <span className="optional"> (optional)</span>}
        </h1>

        {currentQuestion.helpText && (
          <p className="help-text">{currentQuestion.helpText}</p>
        )}

        <div className="input-wrapper">
          {currentQuestion.type === 'textarea' ? (
            <textarea
              ref={inputRef}
              value={currentValue}
              onChange={(e) => setCurrentValue(e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows="6"
              className="input-field"
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={currentValue}
              onChange={(e) => setCurrentValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={currentQuestion.placeholder}
              className="input-field"
            />
          )}
        </div>

        <div className="button-row">
          {currentStep > 0 && (
            <button className="back-btn" onClick={handleBack}>
              ← Back
            </button>
          )}
          
          <button 
            className="next-btn" 
            onClick={handleNext}
            disabled={currentQuestion.required && !currentValue.trim()}
          >
            {currentStep === QUESTIONS.length - 1 ? (
              isSubmitting ? 'Submitting...' : 'Submit'
            ) : (
              <>Next →</>
            )}
          </button>
        </div>

        <div className="keyboard-hint">
          Press <strong>Enter ↵</strong> to continue
        </div>
      </div>
    </div>
  );
}

export default App;
